"use client";

import { useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Dialog } from "radix-ui";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { ClientView } from "@/components/work/ClientView";
import { pauseScroll, resumeScroll } from "@/lib/lenis";
import type { Client } from "@/lib/work";

const pad = (n: number) => String(n).padStart(2, "0");

const iconButton =
  "grid size-10 place-items-center rounded-btn border border-black/15 text-brand-ink outline-none transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent focus-visible:ring-2 focus-visible:ring-brand-accent";

/**
 * A client's work, in a sheet that rises over the work section.
 *
 * Radix owns the parts that are easy to get subtly wrong: the focus trap, Escape, the
 * portal, `aria-modal` and the inert page behind. Framer owns the motion, through Radix's
 * `forceMount` so the sheet can animate out before it unmounts. Lenis is paused while it is
 * open, or the page would keep gliding behind the backdrop; the sheet's own scroller is
 * marked `data-lenis-prevent` so the wheel scrolls it natively.
 *
 * The left and right arrow keys step to the previous and next client without closing.
 */
export const ClientSheet = ({
  clients,
  index,
  onClose,
  onIndexChange,
}: {
  clients: Client[];
  /** Index into `clients`, or null when closed. */
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}) => {
  const reduce = useReducedMotion();
  const scroller = useRef<HTMLDivElement>(null);
  const open = index !== null;
  const client = open ? clients[index] : undefined;
  const count = clients.length;

  useEffect(() => {
    if (!open) return;
    pauseScroll();
    return () => resumeScroll();
  }, [open]);

  // A new client starts at its top, not wherever the last one was scrolled to.
  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
  }, [index]);

  useEffect(() => {
    if (!open || count < 2) return;
    const onKey = (event: KeyboardEvent) => {
      // Arrow keys inside the tab list move between tabs; leave those to Radix.
      if ((event.target as HTMLElement | null)?.closest('[role="tablist"]')) return;
      if (event.key === "ArrowRight") onIndexChange(((index ?? 0) + 1) % count);
      else if (event.key === "ArrowLeft") onIndexChange(((index ?? 0) - 1 + count) % count);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, index, count, onIndexChange]);

  const step = (delta: number) => index !== null && onIndexChange((index + delta + count) % count);

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <AnimatePresence>
        {client && index !== null && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reduce ? 0 : 0.3 }}
                className="fixed inset-0 z-[60] bg-brand-navy/45 backdrop-blur-md"
              />
            </Dialog.Overlay>
            <Dialog.Content asChild forceMount aria-describedby={undefined}>
              <motion.div
                initial={reduce ? { opacity: 0 } : { y: "100%" }}
                animate={reduce ? { opacity: 1 } : { y: 0 }}
                exit={reduce ? { opacity: 0 } : { y: "100%" }}
                transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 260, damping: 34, mass: 0.9 }}
                className="fixed inset-x-0 bottom-0 top-2 z-[61] flex flex-col overflow-hidden rounded-t-[28px] bg-white shadow-[0_-30px_80px_-30px_rgba(13,27,42,0.5)] outline-none sm:inset-x-3 sm:top-4 lg:inset-x-6"
              >
                <div className="flex shrink-0 items-center justify-between gap-4 border-b border-black/10 px-4 py-3 sm:px-8">
                  <Dialog.Title className="min-w-0 truncate font-mono text-[11px] uppercase tracking-[0.2em] text-brand-ink/60">
                    <span className="text-brand-ink/60">
                      {pad(index + 1)} / {pad(count)}
                    </span>
                    <span className="mx-3" aria-hidden>
                      ·
                    </span>
                    {client.name}
                  </Dialog.Title>
                  <div className="flex shrink-0 items-center gap-2">
                    {count > 1 && (
                      <>
                        <button type="button" onClick={() => step(-1)} aria-label="Previous client" className={iconButton}>
                          <ArrowLeft className="size-4" />
                        </button>
                        <button type="button" onClick={() => step(1)} aria-label="Next client" className={iconButton}>
                          <ArrowRight className="size-4" />
                        </button>
                      </>
                    )}
                    <Dialog.Close aria-label="Close" className={`${iconButton} ml-2 border-brand-ink bg-brand-ink text-white hover:bg-brand-accent hover:text-white`}>
                      <X className="size-4" />
                    </Dialog.Close>
                  </div>
                </div>

                <div ref={scroller} data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={client.slug}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: reduce ? 0 : 0.3, ease: [0.16, 1, 0.3, 1] }}
                      className="mx-auto max-w-[1400px] px-4 py-8 sm:px-8 sm:py-12"
                    >
                      <ClientView client={client} />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
};
